import React from 'react';
import { Linkedin, Twitter, Mail, MapPin, ExternalLink, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { isArabic } = useLanguage();
  const currentYear = new Date().getFullYear();

  const t = {
    en: {
      desc: "AFAM Group is a premier multi-sector retail and hospitality group, uniting artisanal bakeries, high-end supermarkets, and premium markets under one vision of excellence.",
      links: "Quick Links",
      hq: "Headquarter",
      inquiries: "Inquiries",
      inqDesc: "Request a capability statement or technical proposal.",
      btn: "Request Info",
      rights: "All Rights Reserved."
    },
    ar: {
      desc: "مجموعة أقام هي مجموعة رائدة في قطاع التجزئة والضيافة، تجمع بين المخابز الحرفية والسوبر ماركت الراقية والأسواق المتميزة تحت رؤية واحدة من التميز.",
      links: "روابط سريعة",
      hq: "المقر الرئيسي",
      inquiries: "الاستفسارات",
      inqDesc: "اطلب بيان القدرات أو المقترح الفني.",
      btn: "طلب معلومات",
      rights: "جميع الحقوق محفوظة."
    }
  };

  const content = isArabic ? t.ar : t.en;

  return (
    <footer className="relative bg-corp-blue text-white pt-20 pb-10 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/glamorous-panties.png')]"></div>
      </div>
      
      {/* Top Accent Line */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-saudi-green/50 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16 ${isArabic ? 'text-right' : 'text-left'}`}>

          {/* Brand Column */}
          <div className="space-y-6">
            <div className={`flex items-center gap-3 group ${isArabic ? 'flex-row-reverse' : ''}`}>
               <span className="text-3xl font-black tracking-tighter">
                <span className="text-red-600">AFAM</span>
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed font-medium">
              {content.desc}
            </p>
            <div className={`flex gap-3 ${isArabic ? 'justify-end' : ''}`}>
              {[Linkedin, Twitter, Mail].map((Icon, i) => (
                <a key={i} href="hjkh" className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-xl hover:bg-saudi-green hover:scale-110 transition-all duration-300">
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-sm flex items-center gap-2">
              <span className="w-6 h-[2px] bg-saudi-green"></span>
              {content.links}
            </h4>
            <ul className="space-y-4">
              {['About', 'Branches', 'Team', 'Contact'].map((item) => (
                <li key={item}>
                  <a href={`#${item.toLowerCase()}`} className="text-gray-400 text-sm hover:text-white flex items-center gap-2 group transition-colors">
                    <ArrowRight size={14} className={`text-saudi-green transition-transform ${isArabic ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div>
            <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-sm flex items-center gap-2">
              <span className="w-6 h-[2px] bg-saudi-green"></span>
              {content.hq}
            </h4>
            <div className="space-y-6">
              <div className={`flex gap-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className="w-10 h-10 shrink-0 bg-white/5 rounded-lg flex items-center justify-center text-saudi-green">
                   <MapPin size={20} />
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                   Fatimah Al Zahraa, Jarir, Al Malaz<br />
                   <span className="text-white font-bold">Riyadh, Saudi Arabia</span>
                </p>
              </div>
              <div className={`flex gap-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <div className="w-10 h-10 shrink-0 bg-white/5 rounded-lg flex items-center justify-center text-saudi-green">
                   <Mail size={20} />
                </div>
                <p className="text-gray-400 text-sm flex items-center">contact@afamksa.com</p>
              </div>
            </div>
          </div>

          {/* Newsletter/Action */}
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
            <h4 className="text-white font-bold mb-4 text-sm">{content.inquiries}</h4>
            <p className="text-gray-400 text-xs mb-6 leading-relaxed">
              {content.inqDesc}
            </p>
            <button className="w-full bg-saudi-green hover:bg-white hover:text-corp-blue text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 group">
              {content.btn} 
              <ExternalLink size={16} className="group-hover:rotate-12 transition-transform" />
            </button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold">
  © {currentYear} AFAM Group. {content.rights} | 
  <span className="ml-2">Designed by </span>
  <a 
    href="https://shdeepu.github.io/" 
    target="_blank" 
    rel="noopener noreferrer" 
    className="text-gray-400 hover:text-saudi-green transition-all duration-300 border-b border-transparent hover:border-saudi-green pb-0.5"
  >
    Md Sazzad Hossen
  </a>
</p>
          <div className="flex gap-8 text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold">
            <a href="nill" className="hover:text-saudi-green transition-colors">Privacy Policy</a>
            <a href="nill" className="hover:text-saudi-green transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;




// import React from 'react';
// import { Linkedin, Twitter, Mail, MapPin, ExternalLink } from 'lucide-react';

// const Footer = () => {
//   const currentYear = new Date().getFullYear();

//   return (
//     <footer className="bg-corp-blue text-white pt-16 pb-8">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">

//           <div className="space-y-6">
//             <div className="flex items-center gap-3 group">
//               <div className="relative">
//                 <img
//                 src="/afam3.ico" 
//                 alt="AFAM Logo" 
//                 className="w-10 h-8 object-contain p-1.5 rounded-lg bg-white/10 backdrop-blur-sm 
//                           border border-white/10 shadow-lg transition-transform duration-500 
//                           group-hover:scale-110 group-hover:shadow-[0_0_15px_rgba(0,128,0,0.4)]"
//                 />
//               </div>
//               <span className="text-2xl font-semibold tracking-[0.2em] text-white transition-all duration-300 group-hover:text-saudi-green">
//                 AFAM </span>
//                 </div>
//                 <p className="text-gray-400 text-sm leading-relaxed">
//                   AFAM Group is a premier multi-sector retail and hospitality group, uniting artisanal bakeries, high-end supermarkets, distinguished restaurants, 
//                   and premium meat, fish, and grocery markets under one vision of excellence.
//                 </p>

//             <div className="flex gap-4">
//               <a href="https://linkedin.com/" className="p-2 bg-white/5 hover:bg-saudi-green rounded-full transition-colors"><Linkedin size={18} /></a>
//               <a href="https://twiteer.com/" className="p-2 bg-white/5 hover:bg-saudi-green rounded-full transition-colors"><Twitter size={18} /></a>
//               <a href="https://gmail.com/" className="p-2 bg-white/5 hover:bg-saudi-green rounded-full transition-colors"><Mail size={18} /></a>
//             </div>
//           </div>

//           {/* Quick Links */}
//           <div>
//             <h4 className="text-lg font-bold mb-6 border-b border-saudi-green w-fit pb-1">Quick Links</h4>
//             <ul className="space-y-4 text-gray-400 text-sm">
//               <li><a href="#about" className="hover:text-saudi-green transition-colors">About AFAM</a></li>
//               <li><a href="#services" className="hover:text-saudi-green transition-colors">Our Services</a></li>
//               <li><a href="#team" className="hover:text-saudi-green transition-colors">Leadership</a></li>
//               <li><a href="#contact" className="hover:text-saudi-green transition-colors">Contact Us</a></li>
//             </ul>
//           </div>

//           {/* Contact Details */}
//           <div>
//             <h4 className="text-lg font-bold mb-6 border-b border-saudi-green w-fit pb-1">Headquarters</h4>
//             <ul className="space-y-4 text-gray-400 text-sm">
//               <li className="flex items-start gap-3">
//                 <MapPin size={18} className="text-saudi-green shrink-0" />
//                 <span>Fatimah Al Zahraa, Jarir, Al Malaz<br />Riyadh, Saudi Arabia</span>
//               </li>
//               <li className="flex items-center gap-3">
//                 <Mail size={18} className="text-saudi-green shrink-0" />
//                 <span>contact@afamksa.com</span>
//               </li>
//             </ul>
//           </div>

//           {/* Newsletter/Action */}
//           <div>
//             <h4 className="text-lg font-bold mb-6 border-b border-saudi-green w-fit pb-1">Inquiries</h4>
//             <p className="text-gray-400 text-sm mb-4">Request a capability statement or technical proposal.</p>
//             <button className="w-full bg-saudi-green text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 transition-colors">
//               Request Info <ExternalLink size={16} />
//             </button>
//           </div>

//         </div>

//         {/* Bottom Bar */}
//         <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500 uppercase tracking-widest">
//           <p>© {currentYear} Md Sazzad Hossen. All Rights Reserved.</p>
//           <div className="flex gap-8">
//             <a href="https://facebbok.com/" className="hover:text-white transition-colors">Privacy Policy</a>
//             <a href="https://linkedin.com/" className="hover:text-white transition-colors">Terms of Service</a>
//           </div>
//         </div>
//       </div>
//     </footer>
//   );
// };

// export default Footer;